import {
  ButtonMain,
  Dropdown,
  Radio,
} from '@quero-delivery/quero-components-web';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import useMotoboy from '@/hooks/motoboy/useMotoboy';
import { usePlaces as useGetPlaces } from '@/_hooks/use-places';

import styles from './styles.module.scss';
import type { MotoboyDetailsFormType } from './types';
import { ENUM_PERIOD } from './constants';
import PurpleButton from '@/Components/shared/purple-button/PurpleButton';
import useBlockReasons from '@/_features/motoboys/hooks/useBlockReasons';

interface FormToBlockProps {
  onCloseModal: () => void;
  params: MotoboyDetailsFormType | null;
  setParams: (params: MotoboyDetailsFormType) => void;
  setOpenModalConfirm: (value: boolean) => void;
}

function FormToBlock({
  onCloseModal,
  params,
  setParams,
  setOpenModalConfirm,
}: FormToBlockProps) {
  const { motoboyId } = useParams<{ motoboyId: string }>();

  const { motoboy } = useMotoboy(motoboyId!);

  const { reasonsOptions } = useBlockReasons();

  const defaultValues = {
    ...params,
    blockType: params?.blockType || 'PERMANENT',
  };

  const { handleSubmit, control, watch, setValue } =
    useForm<MotoboyDetailsFormType>({
      defaultValues,
    });

  const [reasonToBlock, blockType, period, periodType, placeId] = watch([
    'reasonToBlock',
    'blockType',
    'period',
    'periodType',
    'placeId',
  ]);

  const isDisabledPeriod = blockType === 'PERMANENT';
  const isDisabledBlockTime = !periodType;

  const { isFetching, placesOptionsWithAll } = useGetPlaces({
    cityId: motoboy?.municipioId,
  });

  const hasPeriod =
    period &&
    Object.values(period)?.some(
      value => value !== null && value !== undefined && Number(value) > 0,
    );

  const hasNoPlace = !placeId;

  const isDisabled = useMemo(() => {
    let disabled = false;
    if (reasonToBlock === undefined) disabled = true;
    if (hasNoPlace) disabled = true;
    if (blockType === 'TEMPORARY' && !hasPeriod) disabled = true;
    return disabled;
  }, [reasonToBlock, blockType, hasPeriod]);

  const onSubmit: SubmitHandler<MotoboyDetailsFormType> = async data => {
    setParams(data);
    setOpenModalConfirm(true);
  };

  return (
    <form className={styles.modalMotoboyForm} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.placeContainer}>
        <Controller
          name="placeId"
          control={control}
          render={({ field: { onChange, value } }) => {
            return (
              <Dropdown
                loading={isFetching}
                label="Adicionar novos estabelecimentos ao bloqueio"
                value={value}
                className={styles.placeSelect}
                labelClassName={styles.spanSelect}
                search
                options={placesOptionsWithAll}
                clearable
                placeholder="Digite o nome"
                onChange={(_, data) => onChange(data.value)}
              />
            );
          }}
        />
      </div>
      {!hasNoPlace && (
        <div>
          <Controller
            name="reasonToBlock"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Dropdown
                search
                className={styles.select}
                label="Motivo da suspensão"
                placeholder="Selecione"
                options={reasonsOptions || []}
                value={value}
                onChange={(event, data) => onChange(data.value)}
                labelClassName={styles.spanSelect}
              />
            )}
          />
          <div className={styles.periodContainer}>
            <div>
              <b className={styles.sectionTitle}>Tipo de bloqueio</b>
              <Controller
                name="blockType"
                control={control}
                defaultValue={undefined}
                render={({ field: { value, onChange } }) => (
                  <div className={styles.periodBlockContainer}>
                    <Radio
                      label="Permanente"
                      onChange={() => {
                        onChange('PERMANENT');
                        setValue('period.MINUTES', null);
                        setValue('period.HOUR', null);
                        setValue('period.DAY', null);
                        setValue('periodType', null);
                      }}
                      checked={value === 'PERMANENT'}
                    />
                    <Radio
                      label="Temporário"
                      onChange={() => onChange('TEMPORARY')}
                      checked={value === 'TEMPORARY'}
                    />
                  </div>
                )}
              />
            </div>
            <div>
              <b
                className={classNames(
                  styles.sectionTitle,
                  isDisabledPeriod && styles.sectionTitle__disabled,
                )}
              >
                Período do bloqueio
              </b>
              <div className={styles.periodBlockContainer}>
                <Controller
                  name="periodType"
                  control={control}
                  defaultValue={undefined}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <Radio
                        label="Minutos"
                        onChange={() => {
                          setValue('period.DAY', null);
                          setValue('period.HOUR', null);
                          onChange('MINUTES');
                        }}
                        data-testid="radio-period-minutes"
                        checked={value === 'MINUTES'}
                        disabled={isDisabledPeriod}
                      />
                      <Radio
                        label="Horas"
                        onChange={() => {
                          setValue('period.DAY', null);
                          setValue('period.MINUTES', null);
                          onChange('HOUR');
                        }}
                        data-testid="radio-period-hour"
                        checked={value === 'HOUR'}
                        disabled={isDisabledPeriod}
                      />
                      <Radio
                        label="Dias"
                        onChange={() => {
                          setValue('period.HOUR', null);
                          setValue('period.MINUTES', null);
                          onChange('DAY');
                        }}
                        data-testid="radio-period-day"
                        checked={value === 'DAY'}
                        disabled={isDisabledPeriod}
                      />
                    </>
                  )}
                />
              </div>
            </div>
          </div>
          <div className={styles.inputPeriodContainer}>
            <b
              className={classNames(
                styles.sectionTitle,
                (isDisabledPeriod || isDisabledBlockTime) &&
                  styles.sectionTitle__disabled,
              )}
            >
              Tempo de bloqueio
            </b>
            <div className={styles.periodBlockContainer}>
              <Controller
                name="period.MINUTES"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className={styles.blockTimeContainer}>
                    <input
                      type="number"
                      placeholder="--"
                      value={value || ''}
                      data-testid="input-period-minutes"
                      onChange={event => onChange(event.target.value)}
                      disabled={
                        isDisabledPeriod ||
                        isDisabledBlockTime ||
                        periodType !== ENUM_PERIOD.MINUTES
                      }
                    />
                    <p
                      className={classNames(
                        (isDisabledPeriod ||
                          isDisabledBlockTime ||
                          periodType !== ENUM_PERIOD.MINUTES) &&
                          styles.blockTimeContainer__disabled,
                      )}
                    >
                      Minutos
                    </p>
                  </div>
                )}
              />
              <Controller
                name="period.HOUR"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className={styles.blockTimeContainer}>
                    <input
                      type="number"
                      placeholder="--"
                      data-testid="input-period-hour"
                      value={value || ''}
                      onChange={event => onChange(event.target.value)}
                      disabled={
                        isDisabledPeriod ||
                        isDisabledBlockTime ||
                        periodType !== ENUM_PERIOD.HOUR
                      }
                    />
                    <p
                      className={classNames(
                        (isDisabledPeriod ||
                          isDisabledBlockTime ||
                          periodType !== ENUM_PERIOD.HOUR) &&
                          styles.blockTimeContainer__disabled,
                      )}
                    >
                      Horas
                    </p>
                  </div>
                )}
              />
              <Controller
                name="period.DAY"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className={styles.blockTimeContainer}>
                    <input
                      type="number"
                      placeholder="--"
                      value={value || ''}
                      data-testid="input-period-day"
                      onChange={event => onChange(event.target.value)}
                      disabled={
                        isDisabledPeriod ||
                        isDisabledBlockTime ||
                        periodType !== ENUM_PERIOD.DAY
                      }
                    />
                    <p
                      className={classNames(
                        (isDisabledPeriod ||
                          isDisabledBlockTime ||
                          periodType !== ENUM_PERIOD.DAY) &&
                          styles.blockTimeContainer__disabled,
                      )}
                    >
                      Dias
                    </p>
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      )}
      <div className={styles.buttonsContainer}>
        <ButtonMain
          secondary
          width="26%"
          textFirst="Cancelar"
          onClick={onCloseModal}
        />
        <PurpleButton width="26%" textFirst="Bloquear" disabled={isDisabled} />
      </div>
    </form>
  );
}

export { FormToBlock };
